import React, { useEffect, useState } from 'react';
import Module from '../../../../../layout/Module';
import { useDispatch, useSelector } from 'react-redux';
import usePeriodicalReport from '../../../../../../hooks/periodical-report/usePeriodicalReport';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../../../../../table/DataGrid';
import TextLog from '../../../../../text/TextLog';
import IconTooltipButton from '../../../../../button/IconTooltipButton';
import FilterButtonPeriodicalReport from '../Filter/Filter';
import { toggleDialog } from '../../../../../../store/actions/periodical-report.actions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ButtonDownloadXML from '../../GenerateXML/ButtonDownloadXML';
import { getSettingSubModule } from '../../../../../../services/periodical-report/periodical-report-setting.service';
import { Skeleton } from '@mui/material';

const List = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const permissions = useSelector(
    (state) => state?.session?.permissions?.['periodical-report-generator'] || {}
  );
  const permissionMarking = useSelector((state) => state?.session?.permissions?.['periodical-report-marking']);
  const { isLoading, isLoadingList, getPeriodicalReport } = usePeriodicalReport();

  const list = useSelector((state) => (state?.periodicalReport ? state?.periodicalReport?.list || [] : []));
  const totalList = useSelector((state) =>
    state?.periodicalReport ? state?.periodicalReport?.totalList || 0 : 0
  );

  const [listPeriod, setListPeriod] = useState([]);
  const [isLoadingPeriod, setIsLoadingPeriod] = useState(false);

  const state = useSelector((state) => state?.filter?.periodicalReport);
  const skip = useSelector((state) => state?.datatable?.periodicalReport?.skip || 0);
  const rowsPerPage = useSelector((state) => state?.datatable?.periodicalReport?.rowsPerPage || 50);
  const columnsTable = useSelector((state) => state?.datatable?.periodicalReport?.columnsTable || []);

  useEffect(() => {
    getPeriodicalReport(skip, rowsPerPage, state?.filter);
  }, [rowsPerPage, skip, state, state?.filter]);

  const onRedirect = (redirectTo) => {
    navigate(`/dashboard/periodical-report/${redirectTo}`);
  };

  useEffect(() => {
    const onLoad = async () => {
      setIsLoadingPeriod(true);
      try {
        const response = await getSettingSubModule('period');
        if (response?.data) {
          setListPeriod(response?.data || null);
        }
      } finally {
        setIsLoadingPeriod(false);
      }
    };
    onLoad();
  }, []);

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary justify-between'>
        <FilterButtonPeriodicalReport />
      </Module.BodyHeader>
      <Module.BodyContainer>
        <DataGrid
          id='periodical-report'
          nameModule='periodicalReport'
          rows={list.map((row, index) => {
            const find = listPeriod?.find((period) => period._id === row.period);
            return {
              ...row,
              index: index + 1 + rowsPerPage * skip,
              log: <TextLog updatedAt={row.updatedAt} updatedBy={row.updatedBy} />,
              status: row?.status?.replace(/-/g, ' '),
              period: isLoadingPeriod ? <Skeleton /> :  find?.name,
              current: row.isCurrent ? (
                <FontAwesomeIcon icon='star' className='text-accent' />
              ) : (
                <div className='size-8' />
              ),
              actions: (
                <div className='grid grid-cols-7 items-center gap-1 items-center justify-center'>
                    
                  {permissions?.['generate-xml'] && row?.status === 'closed' && row?.xml ? (
                    <ButtonDownloadXML
                      id={`generate-xml-${row._id}`}
                      xml={row?.xml}
                      className='size-8'
                      loading={isLoading}
                    />
                  ) : (
                    <div className='size-8' />
                  )}
                  {permissionMarking && !['draft'].includes(row.status) ? (
                    <IconTooltipButton
                      id={`marking-${row._id}`}
                      disabled={isLoading}
                      title='Marking'
                      icon='pencil-mechanical'
                      style={{ color: 'var(--accent)' }}
                      onClick={() => onRedirect(`marking/${row._id}`)}
                      className='size-8'
                    />
                  ) : (
                    <div className='size-8' />
                  )}
                  {permissions?.preview ? (
                    <IconTooltipButton
                      id={`preview-${row._id}`}
                      disabled={isLoading}
                      title='Preview'
                      icon='expand-alt'
                      onClick={() => onRedirect(`preview/${row._id}`)}
                      className='size-8'
                    />
                  ) : (
                    <div className='size-8' />
                  )}
                  {permissions?.updated ? (
                    <IconTooltipButton
                      id={`edit-${row._id}`}
                      disabled={isLoading}
                      title='Edit'
                      icon='pen'
                      onClick={() => onRedirect(`edit/${row._id}`)}
                      className='size-8'
                    />
                  ) : (
                    <div className='size-8' />
                  )}
                  {permissions?.cloned ? (
                    <IconTooltipButton
                      id={`clone-${row._id}`}
                      disabled={isLoading}
                      title='Clone'
                      icon='clone'
                      onClick={() => dispatch(toggleDialog('clone', row))}
                      className='size-8'
                    />
                  ) : (
                    <div className='size-8' />
                  )}

                  {['draft', 'closed'].includes(row.status) && permissions.deleted ? (
                    <IconTooltipButton
                      id={`delete-${row._id}`}
                      disabled={isLoading}
                      title='Delete'
                      icon='trash-alt'
                      onClick={() => dispatch(toggleDialog('delete', row))}
                      className='size-8'
                    />
                  ) : (
                    <div className='size-8' />
                  )}
                </div>
              )
            };
          })}
          loading={isLoadingList}
          totalRows={totalList}
          skip={skip}
          rowsPerPage={rowsPerPage}
          columns={[
            { label: '#', key: 'index', align: 'center', width: 10, show: true, hide: false },
            { label: '', key: 'current', align: 'center', width: 10, show: true, hide: false },
            ...columnsTable,
            {
              label: 'Log',
              key: 'log',
              align: 'center',
              className: 'text-center',
              width: 120,
              show: true,
              hide: false
            },
            { label: 'Actions', key: 'actions', align: 'center', width: 50, show: true, hide: false }
          ]}
          labelNoRows="You don't have any periodical report yet"
        />
      </Module.BodyContainer>
    </>
  );
};

export default List;
